// 共通FX素材キット (FX#1 T0)
// 全関数 additive (globalCompositeOperation='lighter') 前提。
// 純描画関数 — ctx を受け取り save/restore で状態を漏らさない。ゲーム状態に依存しない。
// Math.random() 禁止 — 散らばりは hash01(seed) で安定再現する。

// 安定擬似乱数 (同じ seed → 同じ値。フレーム間でチラつかない)
export function hash01(n: number): number {
  const s = Math.sin(n * 127.1) * 43758.5453
  return s - Math.floor(s)   // 0..1
}

// 光の点 (火花/魂火/煤/粉塵の原子)
export function glowMote(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, a: number): void {
  if (a <= 0.01 || r <= 0.4) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, color)
  g.addColorStop(0.35, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.globalAlpha = a
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// 先細りの光すじ (刃光/光条/斬撃残像)
// (x,y) から (dirX,dirY) 正規化方向へ長さ len の光すじ。根元=幅 baseWidth → 先端=幅0 へ細る。
export function taperStreak(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  dirX: number, dirY: number,
  len: number, baseWidth: number,
  color: string, a: number,
): void {
  if (a <= 0.01 || len <= 0.5 || baseWidth <= 0.5) return

  // 方向を正規化
  const mag = Math.sqrt(dirX * dirX + dirY * dirY)
  if (mag < 0.001) return
  const nx = dirX / mag, ny = dirY / mag

  // 法線ベクトル
  const perpX = -ny, perpY = nx

  // 先端座標
  const tipX = x + nx * len, tipY = y + ny * len

  // 根元2点・先端2点 (先端幅はほぼ0で消える)
  const hw = baseWidth * 0.5
  const p0x = x + perpX * hw,  p0y = y + perpY * hw
  const p1x = x - perpX * hw,  p1y = y - perpY * hw
  const p2x = tipX - perpX * 0.5, p2y = tipY - perpY * 0.5
  const p3x = tipX + perpX * 0.5, p3y = tipY + perpY * 0.5

  ctx.save()
  ctx.globalCompositeOperation = 'lighter'

  // 根元→先端の linearGradient (根元 color@a → 先端 透明)
  const g = ctx.createLinearGradient(x, y, tipX, tipY)
  g.addColorStop(0, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')

  ctx.globalAlpha = a
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.moveTo(p0x, p0y)
  ctx.lineTo(p3x, p3y)
  ctx.lineTo(p2x, p2y)
  ctx.lineTo(p1x, p1y)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

// 床に寝た欠けた円弧 (魔法陣/波紋の置換)
// fromA→toA は必ず 2π 未満 (欠けさせる)。crumble > 0 で外周に粒子。
export function floorArc(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  rx: number, ry: number,
  fromA: number, toA: number,
  color: string, a: number,
  width: number,
  crumble = 0,
): void {
  if (a <= 0.01) return

  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = a

  // 楕円弧を描く (Canvas は楕円を scale で表現)
  ctx.save()
  ctx.translate(cx, cy)
  ctx.scale(1, ry / rx)
  ctx.lineWidth = width / (ry / rx)
  ctx.strokeStyle = color
  ctx.beginPath()
  ctx.arc(0, 0, rx, fromA, toA)
  ctx.stroke()
  ctx.restore()

  // crumble: 外周に沿って glowMote を散らす
  if (crumble > 0) {
    const span = toA - fromA
    for (let i = 0; i < crumble; i++) {
      const ang = fromA + span * hash01(i * 7.13 + 1)
      const dist = rx * (0.85 + hash01(i * 3.71) * 0.3)
      const px = cx + Math.cos(ang) * dist
      const py = cy + Math.sin(ang) * (ry / rx * dist / (dist / rx))  // 楕円 y 補正
      const pr = width * (0.4 + hash01(i * 11.5) * 0.6)
      const pa = (0.4 + hash01(i * 5.9) * 0.5)
      // glowMote は ctx の save/restore を持つが already inside save→restore
      // lighter は外の save で設定済みなので globalCompositeOperation は継承される
      const g2 = ctx.createRadialGradient(px, py, 0, px, py, pr)
      g2.addColorStop(0, color)
      g2.addColorStop(0.35, color)
      g2.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.globalAlpha = a * pa
      ctx.fillStyle = g2
      ctx.beginPath()
      ctx.arc(px, py, pr, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  ctx.restore()
}

// 火花の飛散 (命中/カウンター炸裂の置換)
// count 個の glowMote を外へ飛散。大小・速度・寿命を hash01 でばらす。
export function emberBurst(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  count: number,
  color: string,
  age: number, life: number,
  seed: number,
  spread: number,
  speed: number,
): void {
  if (age >= life) return
  const ratio = age / life

  ctx.save()
  ctx.globalCompositeOperation = 'lighter'

  for (let i = 0; i < count; i++) {
    // 方向: spread で角度範囲を制限 (spread=1 で全方向)
    const baseAng = hash01(seed + i) * Math.PI * 2
    const ang = baseAng * spread
    const dist = age * speed * (0.5 + hash01(seed + i + 99))
    const px = x + Math.cos(ang) * dist
    const py = y + Math.sin(ang) * dist

    const r = (4 + hash01(seed + i + 31) * 12) * (1 - ratio * 0.5)
    const a = (1 - ratio) * (0.5 + hash01(seed + i + 57) * 0.5)

    const g = ctx.createRadialGradient(px, py, 0, px, py, r)
    g.addColorStop(0, color)
    g.addColorStop(0.35, color)
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.globalAlpha = a
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(px, py, r, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

// 足元から立ち上る魂火 (周囲オーラの置換)
// count 個の glowMote が足元 footY から上へ漂う。
export function emberRise(
  ctx: CanvasRenderingContext2D,
  cx: number, footY: number,
  count: number,
  color: string,
  t: number,
  seed: number,
  height: number,
  width: number,
): void {
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'

  for (let i = 0; i < count; i++) {
    const speed = 0.4 + hash01(seed + i + 11) * 0.6
    const phase = (t * speed + hash01(seed + i)) % 1
    const py = footY - phase * height
    const px = cx + (hash01(seed + i + 7) - 0.5) * width
    const a = (1 - phase) * (0.4 + hash01(seed + i + 23) * 0.4)
    const r = 3 + hash01(seed + i + 41) * 5

    const g = ctx.createRadialGradient(px, py, 0, px, py, r)
    g.addColorStop(0, color)
    g.addColorStop(0.35, color)
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.globalAlpha = a
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(px, py, r, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}
